import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { UserToken } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { Button, Card, EmptyState, Spinner } from '../../components/ui';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';

const KEY = ['me', 'tokens'];

/** Account page card: the user's own tokens (the Outlook add-in and similar) with a "Remove" button each. */
export function ConnectedAppsCard() {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: KEY, queryFn: () => api.get('/me/tokens', { schema: z.array(UserToken) }) });
  const revoke = useMutation({
    mutationFn: (id: string) => api.delete(`/me/tokens/${id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KEY });
      toast(t('connectedApps.revoked'));
    },
  });
  return (
    <Card title={t('connectedApps.title')} description={t('connectedApps.description')}>
      {q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : !q.data ? (
        <Spinner />
      ) : q.data.length === 0 ? (
        <EmptyState message={t('connectedApps.empty')} />
      ) : (
        <>
          <ErrorLine error={revoke.error} />
          <ul className="divide-y divide-slate-200">
            {q.data.map((tok) => (
              <li key={tok.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="font-medium text-slate-900">{tok.label}</p>
                  <p className="text-sm text-slate-600">
                    {[
                      t(`connectedApps.purpose.${tok.purpose}`),
                      t('connectedApps.created', { date: formatDateTime(tok.createdAt) }),
                      tok.lastUsedAt ? t('connectedApps.lastUsed', { date: formatDateTime(tok.lastUsedAt) }) : t('connectedApps.neverUsed'),
                      t('connectedApps.expires', { date: formatDateTime(tok.expiresAt) }),
                    ].join(' · ')}
                  </p>
                </div>
                <Button
                  variant="danger"
                  aria-label={t('connectedApps.revokeFor', { label: tok.label })}
                  disabled={revoke.isPending}
                  onClick={() => revoke.mutate(tok.id)}
                >
                  {t('connectedApps.revoke')}
                </Button>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}
